import { computed, onMounted, onUnmounted, ref, watch, type Ref } from "vue";

import authService from "../services/auth.service.ts";
import { createPollTimer, shouldSuppressBackgroundPolling } from "../utils/backgroundPolling.ts";
import {
  DiscoveredIp,
  DISCOVERY_SAMPLES_PER_TARGET,
  discoveredIpsKey,
  discoveryUrlsFromStatus,
  walkDiscoveryUrls,
} from "../shared/utils/discovery.ts";

// Fallback ports if system status omits them (admin-configurable otherwise).
const DEFAULT_DISCOVERY_PORTS = [443, 222, 3389, 8000];

/**
 * Discovery polling cadence. Poll fast while the discovered set keeps changing,
 * then back off to the slow interval after `stableThreshold` unchanged walks. A
 * fixed fast cadence would trip the discovery host's per-source-IP rate limit.
 */
export interface DiscoveryCadence {
  fastIntervalMs?: number;
  slowIntervalMs?: number;
  stableThreshold?: number;
}

const DEFAULT_CADENCE: Required<DiscoveryCadence> = {
  fastIntervalMs: 10_000,
  slowIntervalMs: 300_000,
  stableThreshold: 2,
};

export interface UseDiscoveredIpsOptions {
  /** The session's primary egress IP; a change re-bursts discovery. */
  currentAddress: Ref<string | undefined>;
  /**
   * Whether the user holds a CGNATOpen knoc. A reactive getter: it gates each
   * walk and is watched, so discovery starts promptly when an admin assigns a
   * CGNATOpen knoc mid-session rather than waiting out the slow cadence.
   */
  hasCgnatOpenKnoc: () => boolean;
  /** Override the default cadence. */
  cadence?: DiscoveryCadence;
}

export interface UseDiscoveredIpsReturn {
  /** Latest discovered IPs, for attaching to a click-to-grant. */
  ips: Ref<DiscoveredIp[]>;
  /** Whether client IP discovery is configured on this server. */
  enabled: Ref<boolean>;
  /** Run one walk + submit now; resolves whether the set changed. */
  runDiscover: () => Promise<boolean>;
}

/**
 * Owns client CGNAT IP discovery: fetches the discovery config, walks the
 * configured ports for the user's egress IPs, submits them, caches the result
 * for click-to-grant, and runs its own stability-driven poll cadence (decoupled
 * from status polling). The walk only runs while the user holds a CGNATOpen
 * knoc and discovery is configured.
 */
export function useDiscoveredIps(options: UseDiscoveredIpsOptions): UseDiscoveredIpsReturn {
  const { currentAddress, hasCgnatOpenKnoc } = options;
  const cadence = { ...DEFAULT_CADENCE, ...options.cadence };

  const ipApis = ref<string[]>([]);
  const ports = ref<number[]>(DEFAULT_DISCOVERY_PORTS);
  const enabled = computed(() => ipApis.value.length > 0);
  const ips = ref<DiscoveredIp[]>([]);

  // Walk state. `generation` discards a walk whose egress IP changed mid-flight.
  let discovering = false;
  let lastSubmittedKey = "";
  let generation = 0;

  // Cadence state.
  let timer: ReturnType<typeof createPollTimer> | null = null;
  let interval = cadence.fastIntervalMs;
  let stableStreak = 0;

  async function runDiscover(): Promise<boolean> {
    // Report "changed" while a walk is in flight so the cadence stays fast
    // rather than backing off mid-discovery.
    if (discovering) return true;
    if (!enabled.value || !hasCgnatOpenKnoc()) return false;

    const startGeneration = generation;
    discovering = true;
    try {
      // Walk every host × port through one throttled pool, sampling each target
      // several times. Split mode probes the v4 and v6 hosts, capturing both
      // egress families; repeated samples also surface a client's alternate
      // egress IPs (multi-WAN/CGNAT). Distinct IPs are unioned below.
      const probes = await walkDiscoveryUrls(ipApis.value, ports.value, undefined, DISCOVERY_SAMPLES_PER_TARGET);

      // The egress IP changed during the walk, so these results belong to the
      // previous network. Discard rather than cache/submit stale IPs.
      if (startGeneration !== generation) return false;

      // Keep each distinct IP once (multiple hosts/ports can report the same
      // address); the port is irrelevant to what gets granted.
      const seen = new Set<string>();
      const results: DiscoveredIp[] = [];
      for (const probe of probes) {
        if (probe.status === "ok" && probe.IP && !seen.has(probe.IP)) {
          seen.add(probe.IP);
          results.push({ Port: probe.Port, IP: probe.IP });
        }
      }
      if (results.length === 0) return false;

      // Skip re-submitting an unchanged set (the common case once IPs settle).
      const key = discoveredIpsKey(results);
      if (key === lastSubmittedKey) return false;

      ips.value = results;
      await authService.submitDiscoveredIps(results);
      lastSubmittedKey = key;
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      discovering = false;
    }
  }

  // Run a walk, then stay fast while changes appear or back off to the slow
  // interval after `stableThreshold` unchanged walks.
  async function cadenceTick(): Promise<void> {
    if (shouldSuppressBackgroundPolling()) return;
    const changed = await runDiscover();
    const previous = interval;
    if (changed) {
      stableStreak = 0;
      interval = cadence.fastIntervalMs;
    } else if (++stableStreak >= cadence.stableThreshold) {
      interval = cadence.slowIntervalMs;
    }
    if (interval !== previous) timer?.start(interval);
  }

  // Re-enter the fast phase and walk now.
  function burst(): void {
    stableStreak = 0;
    interval = cadence.fastIntervalMs;
    timer?.start(interval);
    void cadenceTick();
  }

  // A new egress IP invalidates the cached set: drop it (so a click-to-grant
  // before the next walk doesn't attach the previous network's IPs), bump the
  // generation to discard any in-flight walk, then re-burst.
  watch(currentAddress, (newIP, oldIP) => {
    if (newIP === oldIP) return;
    generation++;
    ips.value = [];
    lastSubmittedKey = "";
    burst();
  });

  // A CGNATOpen knoc can be assigned mid-session (admin change, picked up by the
  // status poll). Burst when one appears so discovery starts now instead of
  // waiting out the slow cadence; removal needs nothing (runDiscover gates it).
  watch(hasCgnatOpenKnoc, (has, hadBefore) => {
    if (has && !hadBefore) burst();
  });

  onMounted(async () => {
    try {
      const { data } = await authService.getSystemStatus();
      ipApis.value = discoveryUrlsFromStatus(data);
      if (data.DiscoverIPPorts?.length > 0) {
        ports.value = data.DiscoverIPPorts;
      }
    } catch (error) {
      console.error(error);
    }
    timer = createPollTimer(cadenceTick);
    timer.start(interval);
    void cadenceTick(); // first walk shortly after mount
  });

  onUnmounted(() => {
    timer?.stop();
    timer = null;
  });

  return { ips, enabled, runDiscover };
}
